import { AfterViewInit, Directive, ElementRef, Injector, Input, Self, effect } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormGroupDirective } from '@angular/forms';
import { ChannelName } from '../../models/interfaces';
import { LoaderBusService } from '../../services/loader-bus/loader-bus.service';

export interface IEnabledCache { [key: string]: boolean }

/**
 * A directive that can be added to a Reactive form group to disable it while the state of a loader channel is true. By default this directive monitors
 * the `global` channel, but the channel can be set using the `channelName` input alongside this directive.
 *
 * @example
 * <form [formGroup]="sampleForm" ofLoaderDisablesFormGroup channelName="http" novalidate>
 *   <mat-form-field>
 *     <input matInput placeholder="Name" name="name" formControlName="Name" type="text">
 *   </mat-form-field>
 * </form>
 */
@Directive({
    selector: '[ofLoaderDisablesFormGroup]',
    standalone: false
})
export class LoaderDisablesFormGroupDirective implements AfterViewInit {
    /** The directive will listen for changes on this channel. */
    @Input() channelName?: ChannelName;
    /** An array of the fields that were disabled before loading, so their disabled states can be preserved after. */
    enabledCache: IEnabledCache = {};

    /** @ignore */
    constructor(
        private _loader: LoaderBusService,
        @Self() private hostFormGroup: FormGroupDirective,
        private _injector: Injector,
        private formEl: ElementRef
    ) {}

    /** @ignore */
    ngAfterViewInit(): void {
        this.populateEnabledCache();
        const channel = this.channelName || 'global';
        const loading = toSignal(
            this._loader._globalChannels[channel].getStatus,
            { initialValue: false, injector: this._injector }
        );
        effect(() => {
            if (loading()) {
                this.disableFields();
            } else {
                this.enableFields();
            }
        }, { injector: this._injector });
    }

    /** Store the current disabled state of each form control. */
    populateEnabledCache(): IEnabledCache {
        const fields = Object.keys(this.hostFormGroup.form.controls);
        fields.forEach((fieldName) => {
            this.enabledCache[fieldName] = !(this.hostFormGroup.form.controls[fieldName].status === 'DISABLED');
        });
        return this.enabledCache;
    }

    disableFields(): void {
        this.hostFormGroup.control.disable();
    }

    enableFields(): void {
        for (const field in this.enabledCache) {
            if (this.enabledCache[field]) {
                this.hostFormGroup.form.controls[field].enable();
            }
        }
    }
}
